import { Primary16SemiBold, Box, Grid } from '@sequencehq/core-components'
import { useBillingScheduleAggregate } from 'modules/BillingScheduleHome/utils/useBillingScheduleAggregate'
import { useBillingScheduleId } from 'modules/BillingScheduleHome/utils/useBillingScheduleId'
import { ScheduleDetailItems } from './ScheduleDetailItems'
import { SectionError } from '../../common/SectionError'
import { Skeleton } from '@sequencehq/core-components'
import fill from 'lodash/fill'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

const useScheduleDetailsSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      const isCancelled = data.billingSchedule.status === 'CANCELLED'

      return {
        customerId: data.billingSchedule.customerId,
        customerContacts: data.customerContacts.map(contact => ({
          id: contact.id,
          name: contact.name,
          email: contact.email
        })),
        startDate: data.billingSchedule.startDate,
        endDate: data.billingSchedule.endDate,
        // A schedule only reaches CANCELLED status via the archive flow, which
        // stamps archivedAt in the same transaction — so for cancelled
        // schedules archivedAt is the moment of cancellation.
        cancelledDate:
          isCancelled && data.billingSchedule.archivedAt
            ? new Date(data.billingSchedule.archivedAt)
            : undefined,
        paymentMethod: data.billingScheduleSettings?.paymentProvider,
        autoChargeCustomer: data.billingScheduleSettings?.autoCharge,
        autoIssueInvoices: data.billingSchedule.autoIssueInvoices,
        purchaseOrderNumber: data.billingSchedule.purchaseOrderNumber,
        label: data.billingSchedule.label,
        scheduleDefaultDueDateDays: data.billingSchedule.defaultDueDateDays,
        memo: data.billingSchedule.memo,
        customFields: Object.entries(
          data.billingSchedule.customFields ?? {}
        ).map(([key, value]) => ({ key, value }))
      }
    }
  })

  const merchantQuery = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount,
    undefined,
    {
      select: data => {
        return {
          merchantDefaultDueDateDays: data?.defaultDueDateDays
        }
      }
    }
  )

  return {
    isLoading:
      billingScheduleAggregateQuery.isPending || merchantQuery.isPending,
    isError: billingScheduleAggregateQuery.isError || merchantQuery.isError,
    ...billingScheduleAggregateQuery.data,
    ...merchantQuery.data
  }
}

export const ScheduleDetailsSection = () => {
  const hook = useScheduleDetailsSection()

  if (hook.isError) {
    return (
      <Box>
        <Box {...Primary16SemiBold} marginBottom="16px">
          Schedule details
        </Box>
        <Box padding="16px" overflow="hidden">
          <SectionError
            size="medium"
            text="An error occurred when loading the schedule details"
          />
        </Box>
      </Box>
    )
  }

  if (hook.isLoading) {
    return (
      <Box>
        <Box {...Primary16SemiBold} marginBottom="16px">
          Schedule details
        </Box>
        <Grid templateColumns="1fr 1fr" gap="128px">
          <Grid
            templateColumns="158px 1fr"
            gridAutoFlow="row"
            templateRows="32px 32px 32px"
            gridColumnGap="16px"
            gridRowGap="8px"
          >
            {fill(Array(6), null).map((_, idx) => (
              <Skeleton height="32px" width="100%" key={idx} />
            ))}
          </Grid>
          <Grid
            templateColumns="158px 1fr"
            templateRows="32px 32px 32px 32px"
            gridAutoFlow="row"
            gridColumnGap="16px"
            gridRowGap="8px"
            height="fit-content"
          >
            {fill(Array(8), null).map((_, idx) => (
              <Skeleton height="32px" width="100%" key={idx} />
            ))}
          </Grid>
        </Grid>
      </Box>
    )
  }

  return (
    <Box padding="8px 0" data-testid="schedule.details">
      <Box {...Primary16SemiBold} marginBottom="16px">
        Schedule details
      </Box>
      <Grid templateColumns="1fr 1fr" gridColumnGap="16px">
        <Box>
          <ScheduleDetailItems.Customer customerId={hook.customerId} />
          {Boolean(hook.customerContacts?.length) && (
            <ScheduleDetailItems.BillingContacts
              contacts={hook.customerContacts}
            />
          )}
          <ScheduleDetailItems.StartDate startDate={hook.startDate} />
          <ScheduleDetailItems.EndDate endDate={hook.endDate} />
          {hook.cancelledDate && (
            <ScheduleDetailItems.CancelledDate
              cancelledDate={hook.cancelledDate}
            />
          )}
        </Box>
        <Box>
          {hook.paymentMethod === 'STRIPE' && (
            <>
              <ScheduleDetailItems.PaymentMethod paymentMethod="STRIPE" />
              <ScheduleDetailItems.AutoChargeCustomer
                autoChargeCustomer={hook.autoChargeCustomer}
              />
            </>
          )}
          {hook.paymentMethod === 'GOCARDLESS' && (
            <ScheduleDetailItems.PaymentMethod paymentMethod="GOCARDLESS" />
          )}
          <ScheduleDetailItems.AutoIssueInvoices
            autoIssueInvoices={hook.autoIssueInvoices}
          />
          <ScheduleDetailItems.DefaultDueDateDays
            defaultDueDateDays={getPaymentTerms({
              scheduleDefaultDueDateDays: hook.scheduleDefaultDueDateDays,
              merchantDefaultDueDateDays: hook.merchantDefaultDueDateDays
            })}
          />
          <ScheduleDetailItems.Memo memo={hook.memo} />
          <ScheduleDetailItems.PurchaseOrderNumber
            purchaseOrderNumber={hook.purchaseOrderNumber}
          />
          <ScheduleDetailItems.Label label={hook.label} />
          {hook.customFields && hook.customFields.length > 0 && (
            <Box data-testid="schedule.details.customFields">
              {hook.customFields.map(field => (
                <ScheduleDetailItems.CustomField
                  key={field.key}
                  fieldKey={field.key}
                  value={field.value}
                />
              ))}
            </Box>
          )}
        </Box>
      </Grid>
    </Box>
  )
}

const getPaymentTerms = ({
  scheduleDefaultDueDateDays,
  merchantDefaultDueDateDays
}: {
  scheduleDefaultDueDateDays: number | undefined
  merchantDefaultDueDateDays: number | undefined
}): number | undefined => {
  if (typeof scheduleDefaultDueDateDays === 'number') {
    return scheduleDefaultDueDateDays
  }

  if (typeof merchantDefaultDueDateDays === 'number') {
    return merchantDefaultDueDateDays
  }

  return undefined
}
